import React, { useState } from 'react';
import { Card, Row, Timeline, Col, Tooltip, Avatar, Grid, Segmented } from 'antd';
import { PrimaryTableRowText } from '../../../../shared/globalStyling/styledText';
import moment from 'moment';
import {
  ArrowDownOutlined,
  ExportOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  LockOutlined,
  UserAddOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  getMobileOperatingSystem,
  getReadableDomain,
  getFaviconUrl,
  getOutboundLink,
  isHtml5Creative,
  getDomainColor,
  isMeaningfulValue,
  stripQueryString,
  formatDuration,
  groupJourneyEvents,
  getCleanUrl,
} from './utils';
import { secondsToTimeText } from '../../utils/secondsToTimeText';
import {
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  FileImageOutlined,
  Html5Outlined,
} from '@ant-design/icons';

const { Item } = Timeline;
const { useBreakpoint } = Grid;

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const sharedStyle = {
  fontFamily: 'roboto',
  fontSize: '105%',
  fontWeight: 700,
  color: '#636363',
};

const touchBadgeStyle = {
  display: 'inline-block',
  fontSize: '10px',
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: '4px',
  marginLeft: '8px',
};

const goalCardStyle = {
  backgroundColor: '#f6ffed',
  border: '1px solid #b7eb8f',
  borderRadius: '6px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const SessionExpand = ({ impressions, journey, record }) => {
  const [viewMode, setViewMode] = useState('story');
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const renderSourceAvatar = (domain, inventoryType) => {
    const url = getFaviconUrl(domain, inventoryType);
    if (url) {
      return (
        <Avatar
          shape="square"
          size={40}
          icon={<CloudOutlined />}
          src={url}
          style={{ flexShrink: 0 }}
        />
      );
    }
    if (domain && domain.startsWith('In-App #')) {
      return (
        <Avatar
          shape="square"
          size={40}
          style={{
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          #
        </Avatar>
      );
    }
    if (domain && domain !== 'Unknown Domain') {
      return (
        <Avatar
          shape="square"
          size={40}
          style={{
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {domain.charAt(0).toUpperCase()}
        </Avatar>
      );
    }
    return (
      <Avatar
        shape="square"
        size={40}
        icon={<CloudOutlined />}
        style={{ flexShrink: 0 }}
      />
    );
  };

  const formatAttribution = (method) => {
    if (!method) return '';
    let parsed = method;
    if (typeof method === 'string') {
      try {
        parsed = JSON.parse(method);
      } catch (e) {
        return method;
      }
    }
    const key = Object.keys(parsed)[0];
    if (!key) return '';
    return key
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };



  // Reverse impressions so newest (closest to session) is at top
  const reversedImpressions = [...(impressions || [])].reverse();

  const impressionItems = reversedImpressions.map((impression, i) => {
    const {
      IMPRESSION_TIME,
      IMPRESSION_USERAGENT,
      IMPRESSION_PAGE_URL,
      IMPRESSION_PUBLISHERNAME,
      CREATIVEURL,
      CREATIVEID,
    } = impression;

    const { impressionDomain, inventoryType } = getReadableDomain(impression, null);
    const OS = getMobileOperatingSystem(IMPRESSION_USERAGENT);

    // After reverse: i=0 is newest (last touch), i=length-1 is oldest (first touch)
    const isLastTouch = i === 0;
    const isFirstTouch = i === reversedImpressions.length - 1;

    let icon = () => {
      if (
        OS === 'Unknown' ||
        OS === 'Chromebook Computer' ||
        OS === 'Windows Computer' ||
        OS === 'Computer'
      ) {
        return <DesktopOutlined />;
      }
      if (OS === 'iPhone' || OS === 'Android' || OS === 'Windows Phone') {
        return <MobileOutlined />;
      }
      if (OS === 'iPad') {
        return <TabletOutlined />;
      }
      return <DesktopOutlined />;
    };

    let appTypeIcon = () => {
      if (inventoryType === 'Application') {
        if (
          OS === 'iPhone' ||
          OS === 'iPad' ||
          OS === 'iPod' ||
          OS === 'Apple Computer'
        ) {
          return <AppleOutlined />;
        }
        if (OS === 'Android') {
          return <AndroidOutlined />;
        }
        return <MobileOutlined />;
      }
      return <DesktopOutlined />;
    };

    const PUBLISHER = isMeaningfulValue(IMPRESSION_PUBLISHERNAME)
      ? `Published by - ${decodeURI(IMPRESSION_PUBLISHERNAME).replace(/[+_]/g, ' ')}`
      : '';

    const touchBadge = () => {
      if (isFirstTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#e6f7ff', color: '#1890ff' }}>
            First Touch
          </span>
        );
      }
      if (isLastTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#f6ffed', color: '#52c41a' }}>
            Last Touch
          </span>
        );
      }
      return null;
    };

    if (isMobile) {
      return (
        <Item key={`imp-${i}`} dot={<ArrowDownOutlined />}>
          <div style={{ padding: '8px 0', width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                ...sharedStyle,
              }}
            >
              <span>{moment(IMPRESSION_TIME).format('MM/DD/YY HH:mm')}</span>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Impression
                {touchBadge()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <Avatar
                shape="square"
                alt={CREATIVEID}
                size={40}
                icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <FileImageOutlined />}
                src={CREATIVEURL || false}
                style={{
                  backgroundColor:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#e6f7ff' : undefined,
                  color:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#1890ff' : undefined,
                }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#636363' }}>
                  {icon()} {OS}
                </div>
                <div style={{ fontSize: '90%', color: '#636363' }}>
                  {inventoryType}
                  {appTypeIcon()}
                </div>
              </div>
            </div>
            {PUBLISHER && (
              <div style={{ marginBottom: 8, fontSize: '90%', color: '#636363' }}>{PUBLISHER}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderSourceAvatar(impressionDomain, inventoryType)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={impressionDomain}>
                  <div style={{ ...ellipsisStyle, fontWeight: 600, color: '#636363' }}>
                    {impressionDomain}
                  </div>
                </Tooltip>
                {getOutboundLink(impressionDomain, inventoryType, OS) && (
                  <a
                    href={getOutboundLink(impressionDomain, inventoryType, OS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open impression source"
                    title="Open impression source"
                    style={{ fontSize: '90%' }}
                  >
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Item>
      );
    }

    return (
      <Item key={`imp-${i}`} dot={<ArrowDownOutlined />}>
        <Row align="middle" gutter={[16, 8]}>
          <Col span={3}>
            <Avatar
              shape="square"
              alt={CREATIVEID}
              size={50}
              icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <FileImageOutlined />}
              src={CREATIVEURL || false}
              style={{
                backgroundColor:
                  isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#e6f7ff' : undefined,
                color:
                  isHtml5Creative(IMPRESSION_PAGE_URL) && !CREATIVEURL ? '#1890ff' : undefined,
              }}
            />
          </Col>
          <Col span={5}>
            <PrimaryTableRowText
              text={`${moment(IMPRESSION_TIME).format('YYYY-MM-DD HH:mm:ss')}`}
            />
          </Col>
          <Col span={4}>
            <div style={{ fontWeight: 600, color: '#636363' }}>
              Impression
              {touchBadge()}
            </div>
          </Col>
          <Col span={4}>
            <div style={{ fontWeight: 600, color: '#636363' }}>
              {icon()} {OS}
            </div>
          </Col>
          <Col span={3}>
            <div style={{ fontWeight: 600, color: '#636363' }}>
              {inventoryType} {appTypeIcon()}
            </div>
          </Col>
          <Col span={5}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderSourceAvatar(impressionDomain, inventoryType)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={impressionDomain}>
                  <div style={{ ...ellipsisStyle, fontWeight: 600, color: '#636363' }}>
                    {impressionDomain}
                  </div>
                </Tooltip>
                {getOutboundLink(impressionDomain, inventoryType, OS) && (
                  <a
                    href={getOutboundLink(impressionDomain, inventoryType, OS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open impression source"
                    title="Open impression source"
                    style={{ fontSize: '90%' }}
                  >
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Item>
    );
  });

  const renderPageIcon = (type, color) => {
    const style = { fontSize: 18, color };
    switch (type) {
      case 'transaction':
        return <DollarOutlined style={style} />;
      case 'receipt':
        return <CheckCircleOutlined style={style} />;
      case 'payment':
        return <CreditCardOutlined style={style} />;
      case 'cart':
        return <ShoppingCartOutlined style={style} />;
      case 'login':
        return <LockOutlined style={style} />;
      case 'signup':
        return <UserAddOutlined style={style} />;
      default:
        return <FileTextOutlined style={style} />;
    }
  };

  const storyJourneyItems = (() => {
    if (!journey || !journey.length) return null;
    const groups = groupJourneyEvents(journey);

    return groups.map((group, idx) => {
      const { pageType, durationSeconds, hasLinkClick, hasSubmitForm, formEventCount } = group;
      const icon = renderPageIcon(pageType.type, pageType.color);

      let label = pageType.label;
      if (hasSubmitForm) {
        label = pageType.type === 'payment' ? 'Payment Submitted' : `${pageType.label} • Submitted`;
      } else if (hasLinkClick) {
        label = `${pageType.label} • Link Clicked`;
      }

      // Time gap from previous group
      const gapElements = [];
      if (idx > 0) {
        const prevEnd = new Date(groups[idx - 1].endTime).getTime();
        const currStart = new Date(group.startTime).getTime();
        const gapSeconds = Math.round((currStart - prevEnd) / 1000);
        if (gapSeconds > 1) {
          gapElements.push(
            <div key={`gap-${idx}`} style={{ fontSize: '80%', color: '#bfbfbf', padding: '4px 0 4px 28px' }}>
              → {formatDuration(gapSeconds)} later
            </div>
          );
        }
      }

      return (
        <React.Fragment key={`story-${idx}`}>
          {gapElements}
          <Item color={pageType.color} dot={icon}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} sm={5}>
              <div style={{ fontSize: '90%', color: '#636363', fontWeight: 500 }}>
                {moment(group.startTime).format('HH:mm:ss')}
              </div>
              {durationSeconds > 5 && (
                <div style={{ fontSize: '80%', color: '#8c8c8c' }}>
                  +{formatDuration(durationSeconds)}
                </div>
              )}
            </Col>
            <Col xs={24} sm={19}>
              <div style={{ fontWeight: 700, color: '#262626', fontSize: '105%' }}>{label}</div>
              <Tooltip title={group.pageUrl}>
                <div
                  style={{
                    ...ellipsisStyle,
                    color: '#636363',
                    fontSize: '90%',
                    maxWidth: isMobile ? '100%' : '400px',
                  }}
                >
                  {stripQueryString(group.pageUrl)}
                </div>
              </Tooltip>
              {formEventCount > 0 && (
                <div style={{ fontSize: '80%', color: '#8c8c8c', marginTop: 2 }}>
                  {formEventCount} form interaction{formEventCount > 1 ? 's' : ''}
                </div>
              )}
            </Col>
          </Row>
        </Item>
        </React.Fragment>
      );
    });
  })();

  const forensicJourneyItems = journey &&
    journey
      .filter((event) => event.EVENT !== 'record')
      .map((event, idx) => {
        return (
          <Item key={`journey-${idx}`} color="green" dot={<ArrowDownOutlined />}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={8}>
                <PrimaryTableRowText
                  text={`${moment(event.DATE).format('YYYY-MM-DD HH:mm:ss')}`}
                />
              </Col>
              <Col xs={24} sm={8}>
                <PrimaryTableRowText text={event.EVENT} />
              </Col>
              <Col xs={24} sm={8}>
                <Tooltip title={event.PAGE_URL}>
                  <div style={{ ...ellipsisStyle, fontWeight: 600, color: '#636363' }}>
                    {stripQueryString(event.PAGE_URL)}
                  </div>
                </Tooltip>
              </Col>
            </Row>
          </Item>
        );
      });

  return (
    <Card bodyStyle={{ overflowY: 'auto', height: isMobile ? 'auto' : '500px' }}>
      <Timeline mode="left" style={{ padding: '10px', paddingLeft: isMobile ? '20px' : '50px' }}>
        {/* SESSION GOAL at the TOP */}
        <Item dot={<CheckCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />}>
          <div
            style={{
              ...goalCardStyle,
              marginLeft: isMobile ? 0 : '50px',
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
            <div>
              <div
                style={{
                  fontFamily: 'roboto',
                  fontSize: '105%',
                  fontWeight: 700,
                  color: '#389e0d',
                }}
              >
                Session Started
              </div>
              <div style={{ fontSize: '90%', color: '#636363' }}>
                {isMeaningfulValue(record.device) && (
                  <span style={{ marginRight: 8 }}>
                    {record.device === 'Mobile' && <MobileOutlined />}
                    {record.device === 'Tablet' && <TabletOutlined />}
                    {record.device === 'Desktop' && <DesktopOutlined />}
                    {' '}{record.device}
                  </span>
                )}
                {isMeaningfulValue(record.landingPage) && (
                  <span>
                    {isMeaningfulValue(record.device) ? '• ' : ''}
                    Landing:{' '}
                    <Tooltip title={record.landingPage}>
                      <span style={{ cursor: 'help' }}>{getCleanUrl(record.landingPage)}</span>
                    </Tooltip>
                  </span>
                )}
              </div>
              <div style={{ fontSize: '90%', color: '#636363' }}>
                {moment(record.sessionStart).format('YYYY-MM-DD HH:mm:ss')}
                {isMeaningfulValue(record.attributionMethod) ? ` • ${formatAttribution(record.attributionMethod)}` : ''}
              </div>
            </div>
          </div>
        </Item>

        <Item color="green">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Session Journey</span>
            <Segmented
              size="small"
              value={viewMode}
              onChange={(value) => setViewMode(value)}
              options={[
                { label: 'Story', value: 'story' },
                { label: 'All Events', value: 'forensic' },
              ]}
            />
          </div>
        </Item>
        {viewMode === 'story' ? storyJourneyItems : forensicJourneyItems}

        {/* Session Ended */}
        <Item dot={<CheckCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />}>
          <div
            style={{
              ...goalCardStyle,
              marginLeft: isMobile ? 0 : '50px',
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
            <div>
              <div
                style={{
                  fontFamily: 'roboto',
                  fontSize: '105%',
                  fontWeight: 700,
                  color: '#389e0d',
                }}
              >
                Session Ended — {secondsToTimeText(record.durationInSeconds || 0)}
              </div>
              <div style={{ fontSize: '90%', color: '#636363' }}>
                {isMeaningfulValue(record.pageViews) ? `${record.pageViews} page views` : ''}
                {isMeaningfulValue(record.exitPage) && (
                  <span>
                    {isMeaningfulValue(record.pageViews) ? ' • ' : ''}
                    Exit:{' '}
                    <Tooltip title={record.exitPage}>
                      <span style={{ cursor: 'help' }}>{getCleanUrl(record.exitPage)}</span>
                    </Tooltip>
                  </span>
                )}
              </div>
              <div style={{ fontSize: '90%', color: '#636363' }}>
                {moment(record.sessionEnd).format('YYYY-MM-DD HH:mm:ss')}
              </div>
            </div>
          </div>
        </Item>

        {/* Divider between session and impressions */}
        <Item>Impression Journey</Item>
        {!isMobile && (
          <Row
            align="middle"
            gutter={[16, 8]}
            style={{
              paddingTop: '5px',
              paddingBottom: '5px',
              fontWeight: 'bold',
              backgroundColor: '#FAFAFA',
              borderBottom: '1px solid #ddd',
              paddingLeft: '50px',
            }}
          >
            <Col span={3}>Creative</Col>
            <Col span={5}>Time</Col>
            <Col span={4}>Event</Col>
            <Col span={4}>Device</Col>
            <Col span={3}>Inventory</Col>
            <Col span={5}>Source</Col>
          </Row>
        )}
        {impressionItems}
      </Timeline>
    </Card>
  );
};

export default SessionExpand;
